import React, { Component } from 'react'
import { Alert, Button, Input, Modal, Select, Checkbox, message, Tooltip, Spin } from 'antd'
import { TYPE_PARTNER, PARTNER_GROUP, SEWING_PARTNER, RECEIVER_FIELDS, EMPTY_RECEIVER } from '../../../../helpers/typePartner'
import { createPartnerV2, listSku } from "../../../../services/api/3dApi"
import { getPartnerErrorMessage } from '../../helpers/partnerPermission'
import ReceiverFields from './ReceiverFields'

const { Option } = Select

const EMPTY_GROUP = { name: '', staff_count: '' }

class AddPartnerModal extends Component {
    constructor(props) {
        super(props)
        this.state = {
            loading: false,
            error: '',
            name: '',
            type: '',
            hasGroups: false,
            groups: [],
            listGroupDuplicates: [],
            partner_group: undefined,
            staff_count: '',
            productivity: '',
            daily_quota: '',
            production_skus: [],
            receiver: { ...EMPTY_RECEIVER },
            skuOptions: [],
            skuLoading: false,
        }
        this.inputRefs = []
        this._skuRequestId = 0
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.isOpen && this.props.isOpen) {
            this._fetchSkuOptions()
        }
    }

    _fetchSkuOptions = async () => {
        const requestId = ++this._skuRequestId
        this.setState({ skuLoading: true })
        try {
            const { data: res } = await listSku()
            if (requestId !== this._skuRequestId) return
            if (res.success) {
                this.setState({ skuOptions: res.data.filter(i => !/\s/.test(i.sku)) })
            }
        } catch (_) {
            // SKU load thất bại không block form
        } finally {
            if (requestId === this._skuRequestId) this.setState({ skuLoading: false })
        }
    }

    _focusNewEmptyInput = () => {
        setTimeout(() => {
            const lastIndex = this.inputRefs.length - 1
            if (this.inputRefs[lastIndex]) {
                this.inputRefs[lastIndex].focus()
            }
        }, 100)
    }

    _closeModal = () => {
        const { onClose } = this.props
        this.setState({
            error: '',
            name: '',
            type: '',
            hasGroups: false,
            groups: [],
            listGroupDuplicates: [],
            partner_group: undefined,
            staff_count: '',
            productivity: '',
            daily_quota: '',
            production_skus: [],
            receiver: { ...EMPTY_RECEIVER },
        }, () => {
            this.inputRefs = []
            onClose()
        })
    }

    _handleChangeInput = (e) => {
        this.setState({ name: e.target.value })
    }

    _handleChangeSelect = (value) => {
        this.setState({ type: value })
    }

    _handleChangeCheckbox = (e) => {
        const checked = e.target.checked
        this.setState({ hasGroups: checked, groups: checked ? [{ ...EMPTY_GROUP }, { ...EMPTY_GROUP }] : [] }, () => {
            if (checked) {
                setTimeout(() => {
                    if (this.inputRefs && this.inputRefs[0]) {
                        this.inputRefs[0].focus()
                    }
                }, 100)
            }
        })
    }

    _handleAddGroup = () => {
        this.setState(prevState => ({
            groups: [...prevState.groups, { ...EMPTY_GROUP }],
        }), this._focusNewEmptyInput)
    }

    _handleChangeGroupValue = (index, field, value) => {
        const { groups, listGroupDuplicates } = this.state
        const newGroups = groups.map((g, idx) => idx === index ? { ...g, [field]: value } : g)
        const names = newGroups.map(g => g.name)
        const newDuplicates = names.filter(Boolean).length > 1
            ? this._findDuplicates(names)
            : (listGroupDuplicates.includes('') ? [''] : [])
        this.setState({ groups: newGroups, listGroupDuplicates: newDuplicates })
    }

    _handleDeleteGroup = (index, deleteAll) => {
        this.setState({
            groups: deleteAll ? [{ ...EMPTY_GROUP }, { ...EMPTY_GROUP }] : this.state.groups.filter((_, idx) => idx !== index),
            listGroupDuplicates: [],
            error: ''
        })
    }

    _findDuplicates = (arr) => {
        const seen = new Set()
        const duplicates = new Set()
        for (const item of arr) {
            if (!!item && !!item.trim() && seen.has(item.trim())) {
                duplicates.add(item.trim())
            } else {
                seen.add(item.trim ? item.trim() : item)
            }
        }
        return Array.from(duplicates)
    }

    _handleSkuPaste = (e) => {
        const text = e.clipboardData && e.clipboardData.getData('text')
        if (!text) return
        const parsed = text.split(/[\s,]+/).map(s => s.trim()).filter(Boolean)
        if (parsed.length <= 1) return
        e.preventDefault()
        const { production_skus, skuOptions } = this.state
        const validSkus = new Set(skuOptions.map(o => o.sku))
        const toAdd = parsed.filter(s => validSkus.has(s))
        const merged = [...new Set([...production_skus, ...toAdd])]
        this.setState({ production_skus: merged })
    }

    _validateNumbers = () => {
        const { staff_count, productivity, daily_quota, hasGroups, groups } = this.state
        if (!hasGroups && staff_count !== '' && !/^[1-9]\d*$/.test(staff_count)) {
            return 'Số lượng nhân sự phải là số nguyên dương'
        }
        if (productivity !== '' && (isNaN(parseFloat(productivity)) || parseFloat(productivity) < 0 || !/^\d+(\.\d+)?$/.test(productivity))) {
            return 'Năng suất phải là số thập phân >= 0'
        }
        if (daily_quota !== '' && !/^\d+$/.test(daily_quota)) {
            return 'Định mức theo số lượng phải là số nguyên dương lớn hơn hoặc bằng 0'
        }
        if (hasGroups) {
            for (let i = 0; i < groups.length; i++) {
                const sc = groups[i].staff_count
                if (sc !== '' && !/^\d+$/.test(sc)) {
                    return `Nhân sự nhóm sản xuất ${i + 1} phải là số nguyên dương lớn hơn hoặc bằng 0`
                }
            }
        }
        return null
    }

    _createPartner = async () => {
        const { name, type, hasGroups, groups, partner_group, staff_count, productivity, daily_quota, production_skus, receiver } = this.state

        const numError = this._validateNumbers()
        if (numError) {
            this.setState({ error: numError })
            return
        }

        this.setState({ loading: true, error: '', listGroupDuplicates: [] })

        const newGroups = groups.map(g => ({ ...g, name: g.name.trim() })).filter(g => g.name)
        const listGroupDuplicates = this._findDuplicates(newGroups.map(g => g.name))

        if (hasGroups && newGroups.length < 2) {
            this.setState({
                loading: false,
                error: 'Cần nhập ít nhất tên 2 nhóm sản xuất.',
                listGroupDuplicates: [''],
                groups: groups.map(g => ({ ...g, name: g.name.trim() })),
            })
            return
        }

        if (hasGroups && listGroupDuplicates.length > 0) {
            this.setState({
                loading: false,
                error: 'Tên nhóm sản xuất không được trùng nhau.',
                listGroupDuplicates,
                groups: groups.map(g => ({ ...g, name: g.name.trim() })),
            })
            return
        }

        try {
            const payload = {
                name,
                type_partner: type,
                has_groups: hasGroups,
                ...(partner_group                           && { partner_group }),
                ...(!hasGroups && staff_count !== ''        && { staff_count: parseInt(staff_count) }),
                ...(productivity !== ''                     && { productivity: parseFloat(productivity) }),
                ...(daily_quota !== ''                      && { daily_quota: parseInt(daily_quota) }),
                ...(production_skus.length > 0              && { production_skus }),
            }

            // Thông tin nhận hàng chỉ gửi cho đối tác May, bỏ qua ô trống
            if (type === SEWING_PARTNER) {
                RECEIVER_FIELDS.forEach(f => {
                    const value = receiver[f.key].trim()
                    if (value) payload[f.key] = value
                })
            }

            if (hasGroups && newGroups.length > 0) {
                payload.groups = newGroups.map(g => ({
                    name: g.name,
                    ...(g.staff_count !== '' && { staff_count: parseInt(g.staff_count) }),
                }))
            }

            const { data } = await createPartnerV2(payload)
            const { success, error } = data

            if (!success) throw new Error(error)

            this.setState({ loading: false }, () => {
                message.success('Tạo đối tác thành công!')
                this._closeModal()
                this.props.reset()
            })
        } catch (e) {
            this.setState({ loading: false, error: getPartnerErrorMessage(e, 'tạo') })
        }
    }

    _totalStaff = () => {
        return this.state.groups.reduce((sum, g) => sum + (parseInt(g.staff_count) || 0), 0)
    }

    render() {
        const {
            loading, error, name, type, hasGroups, groups, listGroupDuplicates,
            partner_group, staff_count, productivity, daily_quota, production_skus, skuOptions, skuLoading, receiver,
        } = this.state
        const { isOpen } = this.props

        return (
            <Modal
                title="Tạo đối tác"
                visible={isOpen}
                onOk={this._createPartner}
                onCancel={this._closeModal}
                maskClosable={false}
                width={640}
                footer={[
                    <Button key="back" onClick={this._closeModal}>Hủy</Button>,
                    <Button
                        key="submit"
                        type="primary"
                        loading={loading}
                        onClick={this._createPartner}
                        disabled={loading || !name || !type}
                    >
                        Tạo
                    </Button>
                ]}
            >
                {error && <Alert className='mb-3' message={error} type='error' />}

                <div className="row">
                    <div className="col-6 mb-3">
                        <label className='font-weight-bold'>Tên đối tác <span className='text-danger'>*</span></label>
                        <Input
                            value={name}
                            onChange={this._handleChangeInput}
                            placeholder='Tên đối tác'
                            autoFocus
                            allowClear
                        />
                    </div>
                    <div className="col-6 mb-3">
                        <label className='font-weight-bold'>Loại đối tác <span className='text-danger'>*</span></label>
                        <Select
                            style={{ width: '100%' }}
                            value={type || undefined}
                            onChange={this._handleChangeSelect}
                            placeholder='Loại đối tác'
                            allowClear
                        >
                            {TYPE_PARTNER.map((item, index) => (
                                <Option key={index} value={item.value}>{item.name}</Option>
                            ))}
                        </Select>
                    </div>
                </div>

                <div className="row">
                    <div className="col-6 mb-3">
                        <label className='font-weight-bold'>Nhóm đối tác</label>
                        <Select
                            style={{ width: '100%' }}
                            value={partner_group || undefined}
                            onChange={v => this.setState({ partner_group: v })}
                            placeholder='Chọn nhóm đối tác'
                            allowClear
                        >
                            {PARTNER_GROUP.map((item, index) => (
                                <Option key={index} value={item.value}>{item.name}</Option>
                            ))}
                        </Select>
                    </div>
                    <div className="col-6 mb-3">
                        <label className='font-weight-bold'>Số lượng nhân sự (đối tác)</label>
                        {hasGroups ? (
                            <Input
                                disabled
                                value={this._totalStaff()}
                                style={{ width: '100%', background: '#f5f5f5' }}
                            />
                        ) : (
                            <Input
                                value={staff_count}
                                placeholder='VD: 50'
                                style={{ width: '100%' }}
                                onChange={e => {
                                    const v = e.target.value
                                    if (v === '' || /^[1-9]\d*$/.test(v)) this.setState({ staff_count: v })
                                }}
                            />
                        )}
                    </div>
                </div>

                <div className="row">
                    <div className="col-6 mb-3">
                        <label className='font-weight-bold'>Năng suất (%)</label>
                        <Input
                            value={productivity}
                            placeholder='VD: 85.5'
                            onChange={e => {
                                const v = e.target.value
                                if (v === '' || /^(0|[1-9]\d*)(\.\d*)?$/.test(v)) this.setState({ productivity: v })
                            }}
                        />
                    </div>
                    <div className="col-6 mb-3">
                        <label className='font-weight-bold'>Định mức theo số lượng (psc/day)</label>
                        <Input
                            value={daily_quota}
                            placeholder='VD: 1000'
                            onChange={e => {
                                const v = e.target.value
                                if (v === '' || /^(0|[1-9]\d*)$/.test(v)) this.setState({ daily_quota: v })
                            }}
                        />
                    </div>
                </div>

                {/* SKU sản xuất */}
                <div className="mb-3">
                    <label className='font-weight-bold'>SKU sản xuất <span className='font-italic font-weight-normal'>(Nếu không chọn gì mặc định là Tất cả SKU)</span></label>
                    <Spin spinning={skuLoading}>
                        <div onPaste={this._handleSkuPaste}>
                        <Select
                            mode="multiple"
                            style={{ width: '100%' }}
                            value={production_skus}
                            onChange={v => this.setState({ production_skus: v })}
                            placeholder='Chọn hoặc dán danh sách SKU...'
                            allowClear
                            showArrow
                            filterOption={(input, option) =>
                                option.props.children.toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {skuOptions.map(o => (
                                <Option key={o._id} value={o.sku}>{o.sku}</Option>
                            ))}
                        </Select>
                        </div>
                    </Spin>
                </div>

                {/* Nhóm sản xuất */}
                <div className="group">
                    <Checkbox checked={hasGroups} onChange={this._handleChangeCheckbox} className='font-weight-bold'>
                        Nhóm sản xuất
                    </Checkbox>
                    {!!hasGroups && (
                        <>
                            <div className="listGroup my-3">
                                <table>
                                    <thead>
                                        <tr>
                                            <th></th>
                                            <th className='pl-3' style={{ minWidth: 220 }}>Tên nhóm</th>
                                            <th className='pl-3' style={{ width: 100 }}>Nhân sự</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {groups.map((group, idx) => (
                                            <tr key={`groupRow_${idx}`}>
                                                <td>
                                                    <span className='groupIndex'>Nhóm sản xuất {idx + 1}</span>
                                                </td>
                                                <td>
                                                    <Input
                                                        placeholder='Nhập tên nhóm sản xuất'
                                                        style={{ width: 200 }}
                                                        className={`inputGroup mx-3 my-1 ${listGroupDuplicates.includes(group.name.trim()) ? 'Error' : ''}`}
                                                        value={group.name}
                                                        allowClear
                                                        onChange={(e) => this._handleChangeGroupValue(idx, 'name', e.target.value)}
                                                        ref={el => this.inputRefs[idx] = el}
                                                    />
                                                </td>
                                                <td>
                                                    <Input
                                                        placeholder='0'
                                                        style={{ width: 80 }}
                                                        className='mx-3 my-1'
                                                        value={group.staff_count}
                                                        onChange={e => {
                                                            const v = e.target.value
                                                            if (v === '' || /^(0|[1-9]\d*)$/.test(v)) this._handleChangeGroupValue(idx, 'staff_count', v)
                                                        }}
                                                    />
                                                </td>
                                                <td>
                                                    {idx > 0 && (
                                                        <Tooltip title='Xóa'>
                                                            <span>
                                                                <i
                                                                    className="fa fa-trash text-danger cursor-pointer"
                                                                    style={{ fontSize: 17 }}
                                                                    onClick={() => this._handleDeleteGroup(idx)}
                                                                />
                                                            </span>
                                                        </Tooltip>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="d-flex">
                                <Tooltip title='Thêm nhóm sản xuất'>
                                    <span className='text-primary mr-5 cursor-pointer' onClick={this._handleAddGroup}>
                                        <i className="fa fa-plus-circle mr-1" style={{ fontSize: 17 }} />
                                        Thêm nhóm sản xuất
                                    </span>
                                </Tooltip>
                            </div>
                        </>
                    )}
                </div>

                {type === SEWING_PARTNER && (
                    <div className='mt-3'>
                        <ReceiverFields
                            values={receiver}
                            onChange={(key, value) => this.setState(prev => ({ receiver: { ...prev.receiver, [key]: value } }))}
                        />
                    </div>
                )}
            </Modal>
        )
    }
}

export default AddPartnerModal
